import type { AvailableSite } from '@prisma-types';
import type {
  Configuration,
  ConfiguratorStore,
} from '@ui/features/configurator/types';
import { useCurrentSiteConfig } from '@ui/hooks/useCurrentSiteConfig';
import { useEffect, useRef, useState } from 'react';
import type { StoreApi } from 'zustand';

import { ConfiguratorStoreContext } from './context';
import { patchConfiguratorStore } from './helpers/patch-configurator-store';
import { createConfiguratorStore } from './store';

type ConfiguratorStoreProviderProps = {
  children: React.ReactNode;
  configuration: Configuration;
  site: AvailableSite;
};

/**
 * Configurator Store Provider
 *
 * @docs https://docs.pmnd.rs/zustand/previous-versions/zustand-v3-create-context#migration
 */
export function ConfiguratorStoreProvider({
  children,
  configuration,
  site,
}: ConfiguratorStoreProviderProps) {
  const [_, forceUpdate] = useState({});
  const storeRef = useRef<StoreApi<ConfiguratorStore>>();
  const { quotingConfig } = useCurrentSiteConfig();

  if (!storeRef.current) {
    storeRef.current = createConfiguratorStore(
      site,
      configuration,
      quotingConfig.quantityDisplayMagnitude,
    );
  }

  /**
   * DEVELOPMENT ONLY (if HMR is enabled)
   *
   * Patch the store state with the new configuration, preserving form values.
   *
   * Ref: https://vitejs.dev/guide/api-hmr
   */
  useEffect(() => {
    if (import.meta.hot) {
      storeRef.current?.setState(patchConfiguratorStore(configuration));
      forceUpdate({});
    }
  }, [configuration]);

  return (
    <ConfiguratorStoreContext.Provider value={storeRef.current}>
      {children}
    </ConfiguratorStoreContext.Provider>
  );
}
